/**
 * Storage Service
 * Backend issues per-file S3 presigned PUT URLs via /api/Storage/temp-path.
 * Client PUTs the file directly with Content-Type — no SAS splicing, no Azure SDK.
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { STORAGE_ENDPOINTS } from '../endpoints';

/**
 * Get a per-file temp upload metadata (includes presigned PUT URL + temp blob path).
 * @param {string} fileName - Original filename
 * @param {string} contentType - MIME type
 * @returns {Promise<{fileName: string, tempBlobPath: string, uploadUrl: string, contentType: string}>}
 */
export const getTempUploadMetadata = async (fileName, contentType) => {
  const params = new URLSearchParams({ fileName });
  if (contentType) params.append('contentType', contentType);
  return apiRequest(() =>
    axiosInstance.get(`${STORAGE_ENDPOINTS.TEMP_PATH ?? '/api/Storage/temp-path'}?${params.toString()}`)
  );
};

/**
 * Upload a file to S3 via a backend-issued presigned PUT URL.
 *
 * NOTE: Old `(containerUrl, blobPath, ...)` signature is dropped — S3 presigned URLs
 * are bound to a specific object key, so client-side path prediction can't work.
 *
 * @param {File} file - File object to upload
 * @param {Function} [onProgress] - Progress callback (loadedBytes, totalBytes, percentage)
 * @returns {Promise<{blobPath: string, contentType: string}>}
 */
export const uploadFileToBlob = async (file, onProgress) => {
  if (!file) throw new Error('uploadFileToBlob: file is required');

  try {
    const meta = await getTempUploadMetadata(file.name, file.type || 'application/octet-stream');

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', meta.uploadUrl, true);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      if (onProgress) {
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            const percentage = Math.round((ev.loaded / ev.total) * 100);
            onProgress(ev.loaded, ev.total, percentage);
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.onabort = () => reject(new Error('Upload aborted'));

      xhr.send(file);
    });

    console.log(`File uploaded successfully: ${meta.tempBlobPath}`);
    return { blobPath: meta.tempBlobPath, contentType: meta.contentType };
  } catch (error) {
    console.error('Failed to upload file:', error);
    throw new Error(`Upload failed: ${error.message}`);
  }
};

/**
 * Generate a temp blob path hint (client-side timestamp).
 * @deprecated Backend now generates the canonical path; use the value returned by uploadFileToBlob.
 */
export const generateTempBlobPath = (userId, filename) => {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, '').slice(0, 14);
  const sanitizedFilename = filename.replace(/[^a-zA-Z0-9.-]/g, '_');
  return `temp/user-${userId}/${timestamp}/${sanitizedFilename}`;
};

/**
 * Get a presigned upload session.
 * @deprecated Per-file presigned URLs (uploadFileToBlob) replace this.
 */
export const getSasTokenForUpload = async (durationMinutes = 60, organizationId = null) => {
  let url = `${STORAGE_ENDPOINTS.SAS_TOKEN_UPLOAD}?durationMinutes=${durationMinutes}`;
  if (organizationId) url += `&organizationId=${organizationId}`;
  return apiRequest(() => axiosInstance.get(url));
};

export const getStorageConfig = async () => {
  return apiRequest(() => axiosInstance.get(STORAGE_ENDPOINTS.CONFIG));
};

export const validateFile = (file, config) => {
  if (config.maxFileSizeBytes && file.size > config.maxFileSizeBytes) {
    const maxMB = Math.round(config.maxFileSizeBytes / (1024 * 1024));
    return {
      isValid: false,
      error: `File size exceeds maximum allowed size of ${maxMB}MB`,
    };
  }

  if (config.allowedFileExtensions && config.allowedFileExtensions.length > 0) {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const isAllowed = config.allowedFileExtensions.some(
      ext => ext.toLowerCase().replace('.', '') === fileExtension
    );

    if (!isAllowed) {
      return {
        isValid: false,
        error: `File type .${fileExtension} is not allowed. Allowed types: ${config.allowedFileExtensions.join(', ')}`,
      };
    }
  }

  return { isValid: true, error: null };
};

export const moveFile = async (sourcePath, destinationPath, deleteSource = true) => {
  return apiRequest(() =>
    axiosInstance.post(STORAGE_ENDPOINTS.MOVE_FILE, {
      sourcePath,
      destinationPath,
      deleteSource,
    })
  );
};

export const deleteFile = async (blobPath) => {
  return apiRequest(() =>
    axiosInstance.delete(`${STORAGE_ENDPOINTS.DELETE_FILE}?blobPath=${encodeURIComponent(blobPath)}`)
  );
};

export const getFileUrl = async (blobPath, organizationId = null) => {
  let url = `${STORAGE_ENDPOINTS.FILE_URL}?blobPath=${encodeURIComponent(blobPath)}`;
  if (organizationId) url += `&organizationId=${organizationId}`;
  return apiRequest(() => axiosInstance.get(url));
};

export const getStorageUsage = async () => {
  return apiRequest(() => axiosInstance.get(STORAGE_ENDPOINTS.USAGE));
};

export const reconcileStorage = async (organizationId) => {
  return apiRequest(() =>
    axiosInstance.post(STORAGE_ENDPOINTS.RECONCILE(organizationId))
  );
};

export const listFiles = async (prefix = null, searchTerm = null, page = 1, pageSize = 20, sortField = 'uploadedAt', sortDirection = 'desc') => {
  const params = new URLSearchParams();
  if (prefix) params.append('prefix', prefix);
  if (searchTerm) params.append('searchTerm', searchTerm);
  params.append('page', page);
  params.append('pageSize', pageSize);
  params.append('sortField', sortField);
  params.append('sortDirection', sortDirection);

  return apiRequest(() =>
    axiosInstance.get(`${STORAGE_ENDPOINTS.FILES}?${params.toString()}`)
  );
};

export const getDownloadUrl = async (blobPath, durationMinutes = 60, organizationId = null) => {
  const body = { blobPath, durationMinutes };
  if (organizationId) body.organizationId = organizationId;
  return apiRequest(() =>
    axiosInstance.post(`${STORAGE_ENDPOINTS.FILES}/download`, body)
  );
};

const storageService = {
  getTempUploadMetadata,
  uploadFileToBlob,
  generateTempBlobPath,
  getSasTokenForUpload,
  getStorageConfig,
  validateFile,
  moveFile,
  deleteFile,
  getFileUrl,
  getStorageUsage,
  reconcileStorage,
  listFiles,
  getDownloadUrl,
};

export default storageService;
